import { Injectable } from '@angular/core';

/**
 * CitationProcessor - Handles text citation processing
 *
 * This service handles:
 * - Citation stripping from text
 * - Citation formatting
 * - Citation validation
 * - Citation extraction
 *
 * Migrated from ChatService as part of the refactoring
 */
@Injectable({
  providedIn: 'root',
})
export class CitationProcessor {
  constructor() {
    // No dependencies needed for basic citation processing
  }

  /**
   * Strip citations from text
   * Removes OpenAI-style citation markers like 【citation】
   */
  stripCitations(text: string): string {
    if (!text) {
      return text;
    }
    // Remove all OpenAI-style citation markers like 【citation】
    return text.replace(/【[^】]+】/g, '');
  }

  /**
   * Extract citations from text
   * Finds all OpenAI-style citation markers like 【citation】
   */
  extractCitations(text: string): string[] {
    if (!text) {
      return [];
    }
    const matches = text.match(/【[^】]+】/g);
    return matches || [];
  }

  /**
   * Format citations for display
   * Converts citation markers to a readable format
   */
  formatCitations(citations: string[]): string {
    if (!citations || citations.length === 0) {
      return '';
    }
    // Remove the brackets and join with commas
    return citations.map((citation) => citation.replace(/【|】/g, '')).join(', ');
  }

  /**
   * Validate citation format
   * Checks if the citation matches the OpenAI format
   */
  validateCitation(citation: string): boolean {
    if (!citation) {
      return false;
    }
    // Check if it matches OpenAI citation format 【...】
    return /^【[^】]+】$/.test(citation);
  }

  /**
   * Check if text contains citations
   */
  hasCitations(text: string): boolean {
    if (!text) {
      return false;
    }
    return /【[^】]+】/.test(text);
  }

  /**
   * Get citation count in text
   */
  getCitationCount(text: string): number {
    return this.extractCitations(text).length;
  }
}
